import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MenuGroup, MenuPage, PageMeta } from '../models/menu-item.model';
import { LooseArrayEnvelope } from '../models/api-envelope.model';
import { IdentityMenuModuleDto } from '../models/auth-api.model';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { LoggerService } from './logger.service';
import { MenuOrderService } from './menu-order.service';
import { MENU_PAGE_GROUPS, groupItems, pageGroupRoutes } from '../config/menu-page-groups.config';
import { applyMenuOrder } from '../utils/menu-order.utils';
import { dedupeMenuGroups, mapIdentityMenuToGroups, pickIdentifier } from '../utils/menu-groups.utils';
import {
    APP_NAVIGATION,
    AppNavigationEntry,
    AppSubPage,
    DEFAULT_MODULE_ICON,
    MODULE_ICONS,
} from '../config/app-navigation.config';
import { ADMIN_MENU_IDS, ADMIN_MODULE, ADMIN_SECTION, HOME_MENU_ID, ROUTE_CATALOG, RouteCatalogEntry, SHELL_PAGE_META, SHELL_ROUTES } from '../config/route-catalog.config';
import { catalogRouteLink } from '../utils/route-catalog.utils';
import { environment } from '../../../environments/environment';

/** Ids sintéticos de los padres de grupo (no chocan con ids de Identity en el menú). */
const PAGE_GROUP_ID_BASE = 8100;


interface PageAlias {
    module: string;
    section: string;
    page: string;
}

const ADMIN_ALIAS = (page: string): PageAlias => ({ module: ADMIN_MODULE, section: ADMIN_SECTION, page });
const MASTER_DATA_ALIAS: PageAlias = { module: 'mercadeo', section: 'auditoria-retail', page: 'datos_maestros' };

/** Nombres alternativos de página (en minúsculas) que Identity puede enviar → página del catálogo. */
const PAGE_NAME_ALIASES: Readonly<Record<string, PageAlias>> = {
    usuarios: ADMIN_ALIAS('usuarios'),
    users: ADMIN_ALIAS('usuarios'),
    roles: ADMIN_ALIAS('roles'),
    paginas: ADMIN_ALIAS('paginas'),
    pages: ADMIN_ALIAS('paginas'),
    'datos maestros': MASTER_DATA_ALIAS,
    'datos-maestros': MASTER_DATA_ALIAS,
    datos_maestros: MASTER_DATA_ALIAS,
    'master data': MASTER_DATA_ALIAS,
};

@Injectable({ providedIn: 'root' })
export class MenuService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly permissionService = inject(PermissionService);
    private readonly logger = inject(LoggerService);
    private readonly menuOrder = inject(MenuOrderService);

    readonly menuGroups = signal<MenuGroup[]>([]);
    private pageIdByRoute = new Map<string, number>();

    // ── Grupos plegables dentro de una sección (p. ej. "Reportes") ─────────────
    // FUENTE ÚNICA: MENU_PAGE_GROUPS (core/config/menu-page-groups.config.ts), la misma
    // que usa el árbol de /admin/paginas. Aquí se agrupa por ruta.
    private readonly pageGroupsByRoute = MENU_PAGE_GROUPS.map((group, index) => ({
        group,
        id: PAGE_GROUP_ID_BASE + index,
        routes: new Set(pageGroupRoutes(group).map((route) => this.normalizeRoute(route).toLowerCase())),
    }));

    /**
     * Mete las páginas de cada grupo bajo un padre plegable. Las páginas conservan el orden
     * de la sección (el orden guardado del menú) y el padre queda donde estaba la primera.
     */
    private groupPages(groups: MenuGroup[]): MenuGroup[] {
        const routeOf = (page: MenuPage): string => this.normalizeRoute(page.routerLink ?? '').toLowerCase();

        return groups.map((menuGroup) => ({
            ...menuGroup,
            sections: menuGroup.sections.map((section) => {
                let pages = section.pages;
                for (const { group, id, routes } of this.pageGroupsByRoute) {
                    pages = groupItems(
                        pages,
                        (page) => !page.children && routes.has(routeOf(page)),
                        (members): MenuPage => ({
                            id,
                            label: group.label,
                            icon: group.icon,
                            permissions: [],
                            children: members.map((page) => ({ ...page, children: undefined })),
                        }),
                    );
                }
                return pages === section.pages ? section : { ...section, pages };
            }),
        }));
    }

    /** Ícono de cada página por ruta. FUENTE ÚNICA: APP_NAVIGATION (el mismo del header). */
    private readonly iconByRoute = new Map(
        APP_NAVIGATION.filter((entry) => !!entry.icon).map((entry) => [
            this.normalizeRoute(entry.routerLink).toLowerCase(),
            entry.icon as string,
        ]),
    );

    loadMenuItems(): Observable<MenuGroup[]> {
        return this.menuOrder
            .load()
            .pipe(
                switchMap((order) =>
                    this.http
                        .get<LooseArrayEnvelope<IdentityMenuModuleDto>>(`${environment.identityApiUrl}/identity/user/menu`)
                        .pipe(map((response) => applyMenuOrder(response.results ?? [], order))),
                ),
                map((modules) => {
                    this.pageIdByRoute = this.buildPageIdByRoute(modules);
                    return this.mapIdentityMenuToGroups(modules);
                }),
                map((groups) => this.mergeAdminMenuEntries(groups)),
                map((groups) => dedupeMenuGroups(groups)),
                map((groups) => {
                    const resolved = groups.length > 0 ? groups : this.buildFallbackMenu();
                    if (groups.length === 0) {
                        this.pageIdByRoute = new Map<string, number>();
                    }
                    const withChildren = this.withHomeEntry(this.groupPages(resolved));
                    this.menuGroups.set(withChildren);
                    return withChildren;
                }),
                catchError((error) => {
                    this.logger.error('MenuService: Failed to load /identity/user/menu, using fallback', error);
                    this.pageIdByRoute = new Map<string, number>();
                    const fallback = this.withHomeEntry(this.groupPages(this.buildFallbackMenu()));
                    this.menuGroups.set(fallback);
                    return of(fallback);
                }),
            );
    }

    getCurrentPageId(url: string = this.router.url): number | null {
        const currentPath = this.normalizeRoute(url);
        let currentPageId: number | null = null;
        let longestMatchLength = -1;

        for (const [route, pageId] of this.pageIdByRoute.entries()) {
            if (!this.matchesRoute(route, currentPath) || route.length <= longestMatchLength) {
                continue;
            }

            currentPageId = pageId;
            longestMatchLength = route.length;
        }

        return currentPageId;
    }

    /**
     * Resuelve el título e icono de la página actual para el header. FUENTE ÚNICA: primero el
     * catálogo de rutas (APP_NAVIGATION + admin, con sus subpáginas), luego el menú cargado y
     * por último las rutas del shell.
     */
    resolvePageMeta(url: string = this.router.url): PageMeta {
        const currentPath = this.normalizeRoute(url);

        let bestEntry: RouteCatalogEntry | null = null;
        let bestLength = -1;
        for (const entry of ROUTE_CATALOG) {
            const route = this.normalizeRoute(entry.routerLink);
            if (this.matchesRoute(route, currentPath) && route.length > bestLength) {
                bestEntry = entry;
                bestLength = route.length;
            }
        }

        if (bestEntry) {
            return this.catalogPageMeta(bestEntry, currentPath);
        }

        for (const group of this.menuGroups()) {
            for (const section of group.sections) {
                for (const page of section.pages) {
                    if (page.routerLink && this.normalizeRoute(page.routerLink) === currentPath) {
                        return { title: page.label, icon: page.icon ?? group.icon ?? 'pi pi-circle' };
                    }
                }
            }
        }

        const shellMeta = SHELL_PAGE_META[currentPath];
        if (shellMeta) {
            return { title: shellMeta.title, icon: shellMeta.icon };
        }

        return { title: '', icon: 'pi pi-circle' };
    }

    /** Título e icono de una página del catálogo; una subpágina (crear/editar) muestra la suya. */
    private catalogPageMeta(entry: RouteCatalogEntry, currentPath: string): PageMeta {
        const navEntry = APP_NAVIGATION.find((candidate) => candidate.routerLink === entry.routerLink);
        const subPage = this.matchSubPage(navEntry, currentPath);

        const moduleIcon = navEntry?.icon ?? this.getIconForModule(entry.moduleIdentifier);
        return {
            title: subPage?.label ?? entry.pageLabel ?? '',
            icon: subPage?.icon ?? moduleIcon,
            description: subPage ? undefined : navEntry?.description,
        };
    }

    /** Subpágina declarada en el catálogo cuyo patrón (`:param` = cualquier valor) casa con la ruta. */
    private matchSubPage(entry: AppNavigationEntry | undefined, currentPath: string): AppSubPage | undefined {
        const base = this.normalizeRoute(entry?.routerLink ?? '');
        if (!entry?.subPages || !currentPath.startsWith(`${base}/`)) {
            return undefined;
        }
        const rest = currentPath.slice(base.length + 1).split('/');
        return entry.subPages.find((subPage) => {
            const pattern = subPage.path.split('/');
            return pattern.length === rest.length && pattern.every((segment, i) => segment.startsWith(':') ? rest[i] !== '' : segment === rest[i]);
        });
    }

    private buildFallbackMenu(): MenuGroup[] {
        const isAdmin = this.authService.isRbiAdmin();
        const canViewUsers = this.permissionService.canViewUsers();

        const visibleEntries = isAdmin
            ? APP_NAVIGATION
            : APP_NAVIGATION.filter((entry) =>
                  this.permissionService.canAccess(
                      entry.moduleIdentifier,
                      entry.sectionIdentifier,
                      entry.pageIdentifier,
                  ),
              );

        const groups = this.mapEntriesToMenu(visibleEntries);
        if (isAdmin) {
            groups.push(this.getAdminMenuGroup());
        } else if (canViewUsers) {
            groups.push(this.getUserManagementMenuGroup());
        }

        return dedupeMenuGroups(groups);
    }

    private mergeAdminMenuEntries(groups: MenuGroup[]): MenuGroup[] {
        if (!this.authService.isRbiAdmin()) {
            return groups;
        }

        const adminGroup = this.getAdminMenuGroup();
        const nextGroups = groups.map((group) => ({
            ...group,
            sections: group.sections.map((section) => ({
                ...section,
                pages: [...section.pages],
            })),
        }));

        const existingAdminGroup = nextGroups.find(
            (group) => group.label.trim().toLowerCase() === adminGroup.label.trim().toLowerCase(),
        );

        if (!existingAdminGroup) {
            return [...nextGroups, adminGroup];
        }

        for (const adminSection of adminGroup.sections) {
            const existingSection = existingAdminGroup.sections.find(
                (section) => section.label.trim().toLowerCase() === adminSection.label.trim().toLowerCase(),
            );

            if (!existingSection) {
                existingAdminGroup.sections.push({
                    ...adminSection,
                    pages: [...adminSection.pages],
                });
                continue;
            }

            for (const adminPage of adminSection.pages) {
                const adminRouterLink = (adminPage.routerLink ?? '').trim().toLowerCase();
                const pageExists = existingSection.pages.some(
                    (page) => (page.routerLink ?? '').trim().toLowerCase() === adminRouterLink,
                );

                if (!pageExists) {
                    existingSection.pages.push({ ...adminPage });
                }
            }
        }

        return nextGroups;
    }

    private mapIdentityMenuToGroups(modules: IdentityMenuModuleDto[]): MenuGroup[] {
        const groups = mapIdentityMenuToGroups(modules, {
            resolveRouterLink: (moduleId, sectionId, pageId, pageName) =>
                this.resolveRouterLink(moduleId, sectionId, pageId, pageName),
            iconForRoute: (routerLink) => this.iconByRoute.get(this.normalizeRoute(routerLink).toLowerCase()),
            iconForModule: (moduleId) => this.getIconForModule(moduleId),
        });
        return this.ensureMercadeoMenuEntries(groups);
    }

    private resolveRouterLink(
        moduleIdentifier: string,
        sectionIdentifier: string,
        pageIdentifier: string,
        pageName?: string,
    ): string | undefined {
        const byIdentifier = catalogRouteLink(moduleIdentifier, sectionIdentifier, pageIdentifier);
        if (byIdentifier) {
            return byIdentifier;
        }

        const normalizedPage = (pageName ?? pageIdentifier).toLowerCase().trim();
        if (normalizedPage === 'dashboard') {
            return SHELL_ROUTES.dashboard;
        }
        const byAlias = PAGE_NAME_ALIASES[normalizedPage];
        if (byAlias) {
            return catalogRouteLink(byAlias.module, byAlias.section, byAlias.page) ?? undefined;
        }

        if (
            moduleIdentifier.toLowerCase().trim() === 'reportes' &&
            sectionIdentifier.toLowerCase().trim() === 'ventas' &&
            pageIdentifier.toLowerCase().trim() === 'mensuales'
        ) {
            return catalogRouteLink('mercadeo', 'auditoria-retail', 'auditorias') ?? undefined;
        }

        return undefined;
    }

    private buildPageIdByRoute(modules: IdentityMenuModuleDto[]): Map<string, number> {
        const pageIds = new Map<string, number>();

        for (const module of modules) {
            const moduleIdentifier = pickIdentifier(module.permissionIdentifier, module.name);

            for (const section of module.sections ?? []) {
                const sectionIdentifier = pickIdentifier(section.permissionIdentifier, section.name);

                for (const page of section.pages ?? []) {
                    if (!this.isValidPageId(page.id)) {
                        continue;
                    }

                    const pageIdentifier = pickIdentifier(page.permissionIdentifier, page.name);
                    const routerLink = this.resolveRouterLink(
                        moduleIdentifier,
                        sectionIdentifier,
                        pageIdentifier,
                        page.name,
                    );

                    if (!routerLink) {
                        continue;
                    }

                    const normalizedRoute = this.normalizeRoute(routerLink);
                    if (!pageIds.has(normalizedRoute)) {
                        pageIds.set(normalizedRoute, page.id);
                    }
                }
            }
        }

        return pageIds;
    }

    private isValidPageId(pageId: number | undefined): pageId is number {
        return typeof pageId === 'number' && Number.isInteger(pageId) && pageId > 0;
    }

    private adminPage(id: number, label: string, page: string): MenuPage {
        return {
            id,
            label,
            routerLink: catalogRouteLink(ADMIN_MODULE, ADMIN_SECTION, page) ?? undefined,
            permissions: [],
            disabled: false,
        };
    }

    /**
     * Enlace «Dashboard» arriba del menú: el dashboard es la portada y no vive en Identity como
     * página navegable. Si Identity trae un módulo con ese nombre, se respeta el suyo.
     */
    private withHomeEntry(groups: MenuGroup[]): MenuGroup[] {
        const yaExiste = groups.some((group) => pickIdentifier(undefined, group.label) === 'dashboard');
        if (yaExiste) {
            return groups;
        }

        const home: MenuGroup = {
            id: HOME_MENU_ID,
            label: SHELL_PAGE_META[SHELL_ROUTES.dashboard].title,
            icon: SHELL_PAGE_META[SHELL_ROUTES.dashboard].icon,
            isGroup: true,
            routerLink: SHELL_ROUTES.dashboard,
            sections: [],
        };
        return [home, ...groups];
    }

    private getAdminMenuGroup(): MenuGroup {
        return {
            id: ADMIN_MENU_IDS.group,
            label: 'ADMINISTRACION',
            icon: 'pi pi-fw pi-cog',
            isGroup: true,
            sections: [
                {
                    id: ADMIN_MENU_IDS.section,
                    label: 'Sistema',
                    pages: [
                        this.adminPage(ADMIN_MENU_IDS.usuarios, 'Usuarios', 'usuarios'),
                        this.adminPage(ADMIN_MENU_IDS.paginas, 'Paginas', 'paginas'),
                        this.adminPage(ADMIN_MENU_IDS.roles, 'Roles', 'roles'),
                    ],
                },
            ],
        };
    }

    private getUserManagementMenuGroup(): MenuGroup {
        return {
            id: ADMIN_MENU_IDS.group,
            label: 'ADMINISTRACION',
            icon: 'pi pi-fw pi-users',
            isGroup: true,
            sections: [
                {
                    id: ADMIN_MENU_IDS.section,
                    label: 'Sistema',
                    pages: [this.adminPage(ADMIN_MENU_IDS.usuarios, 'Usuarios', 'usuarios')],
                },
            ],
        };
    }

    private mapEntriesToMenu(entries: typeof APP_NAVIGATION): MenuGroup[] {
        if (!entries.length) {
            return [];
        }

        const groupsMap = new Map<string, MenuGroup>();
        let nextSectionId = 1000;
        let nextPageId = 10000;

        entries.forEach((entry, index) => {
            const groupKey = entry.moduleIdentifier;
            const group = groupsMap.get(groupKey) ?? {
                id: index + 1,
                label: entry.moduleLabel,
                icon: this.getIconForModule(entry.moduleIdentifier),
                isGroup: true,
                sections: [],
            };

            let section = group.sections.find((item) => item.label === entry.sectionLabel);
            if (!section) {
                section = {
                    id: nextSectionId++,
                    label: entry.sectionLabel,
                    pages: [],
                };
                group.sections.push(section);
            }

            section.pages.push({
                id: nextPageId++,
                label: entry.pageLabel,
                routerLink: entry.routerLink,
                icon: entry.icon,
                permissions: [],
                disabled: false,
            });

            groupsMap.set(groupKey, group);
        });

        return Array.from(groupsMap.values());
    }

    private ensureMercadeoMenuEntries(groups: MenuGroup[]): MenuGroup[] {
        const mercadeoGroup = groups.find(
            (group) => pickIdentifier(undefined, group.label) === 'mercadeo',
        );
        if (!mercadeoGroup) {
            return groups;
        }

        let mercadeoSection = mercadeoGroup.sections.find(
            (section) =>
                pickIdentifier(undefined, section.label) === 'auditoria-retail' ||
                pickIdentifier(undefined, section.label) === 'auditoria retail' ||
                section.pages.some((page) => (page.routerLink ?? '').startsWith('/mercadeo/auditoria-retail/')),
        );

        if (!mercadeoSection) {
            mercadeoSection = {
                id: 7990,
                label: 'Auditoria Retail',
                pages: [],
            };
            mercadeoGroup.sections.push(mercadeoSection);
        } else {
            mercadeoSection.label = 'Auditoria Retail';
        }

        return groups;
    }

    private normalizeRoute(url: string): string {
        const [path] = url.split(/[?#]/, 1);
        if (!path) {
            return '/';
        }

        const normalizedPath = path.trim().replace(/\/+$/, '');
        return normalizedPath || '/';
    }

    private matchesRoute(route: string, currentPath: string): boolean {
        return currentPath === route || currentPath.startsWith(`${route}/`);
    }

    /** Icono del módulo. FUENTE ÚNICA: MODULE_ICONS en app-navigation.config. */
    private getIconForModule(moduleId: string): string {
        return MODULE_ICONS[moduleId.toLowerCase().trim()] ?? DEFAULT_MODULE_ICON;
    }
}
